import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CalendarToday,
  TrendingUp,
  Schedule,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { budgetAPI } from "../../services/api/budget.api";

const BudgetPeriodsPage = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    period_type: "quarterly",
    start_date: "",
    end_date: "",
    submission_deadline: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await budgetAPI.getBudgetPeriods();
      if (response.data.success) {
        setPeriods(response.data.data.budgetPeriods || []);
      }
    } catch (error) {
      console.error("Error fetching budget periods:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Period name is required";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    if (!formData.submission_deadline) {
      newErrors.submission_deadline = "Submission deadline is required";
    }

    // Validate date logic
    if (
      formData.start_date &&
      formData.end_date &&
      formData.submission_deadline
    ) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      const deadline = new Date(formData.submission_deadline);

      if (startDate >= endDate) {
        newErrors.end_date = "End date must be after start date";
      }

      if (deadline >= startDate) {
        newErrors.submission_deadline =
          "Submission deadline must be before start date";
      }

      if (deadline > endDate) {
        newErrors.submission_deadline =
          "Submission deadline cannot be after end date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Clear previous errors and messages
      setErrors({});
      setSubmitError("");
      setSuccessMessage("");

      // Validate form before submission
      if (!validateForm()) {
        setSubmitting(false);
        return;
      }

      if (selectedPeriod) {
        // Update existing period
        await budgetAPI.updateBudgetPeriod(selectedPeriod.id, formData);
        setSuccessMessage("Budget period updated successfully!");
      } else {
        // Create new period
        await budgetAPI.createBudgetPeriod(formData);
        setSuccessMessage("Budget period created successfully!");
      }
      setOpenDialog(false);
      setSelectedPeriod(null);
      setFormData({
        name: "",
        period_type: "quarterly",
        start_date: "",
        end_date: "",
        submission_deadline: "",
      });
      fetchPeriods();
    } catch (error) {
      console.error("Error saving budget period:", error);

      // Handle different types of errors
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Handle validation errors
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const fieldErrors = {};
          errorData.errors.forEach((err) => {
            fieldErrors[err.field] = err.message;
          });
          setErrors(fieldErrors);
        }
        // Handle single error message
        else if (errorData.message) {
          setSubmitError(errorData.message);
        }
        // Handle generic error
        else {
          setSubmitError("Failed to save budget period. Please try again.");
        }
      } else {
        setSubmitError(
          "Network error. Please check your connection and try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "warning";
      case "closed":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Budget Periods Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create Period
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Budget periods define the timeframes for budget submissions. Department
        Heads can only create budgets during active periods.
      </Alert>

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Typography>Loading budget periods...</Typography>
      ) : (
        <Grid container spacing={3}>
          {periods.map((period) => (
            <Grid item xs={12} md={6} lg={4} key={period.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {period.name}
                    </Typography>
                    <Chip
                      label={period.status}
                      color={getStatusColor(period.status)}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TrendingUp sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {period.period_type.charAt(0).toUpperCase() +
                          period.period_type.slice(1)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(period.start_date)} -{" "}
                        {formatDate(period.end_date)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Schedule sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        Deadline: {formatDate(period.submission_deadline)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => {
                        setSelectedPeriod(period);
                        setFormData({
                          name: period.name,
                          period_type: period.period_type,
                          start_date: period.start_date,
                          end_date: period.end_date,
                          submission_deadline: period.submission_deadline,
                        });
                        setOpenDialog(true);
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setErrors({});
          setSubmitError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedPeriod ? "Edit Budget Period" : "Create Budget Period"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* General Error Alert */}
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Period Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Period Type</InputLabel>
              <Select
                value={formData.period_type}
                label="Period Type"
                onChange={(e) =>
                  setFormData({ ...formData, period_type: e.target.value })
                }
              >
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) =>
                setFormData({ ...formData, start_date: e.target.value })
              }
              error={!!errors.start_date}
              helperText={errors.start_date}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) =>
                setFormData({ ...formData, end_date: e.target.value })
              }
              error={!!errors.end_date}
              helperText={errors.end_date}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Submission Deadline"
              type="date"
              value={formData.submission_deadline}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  submission_deadline: e.target.value,
                })
              }
              error={!!errors.submission_deadline}
              helperText={
                errors.submission_deadline ||
                "Deadline must be before the start date"
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setErrors({});
              setSubmitError("");
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Saving..." : selectedPeriod ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetPeriodsPage;
